import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as LucideIcons from 'lucide-react';

const LUCIDE_ICONS = [
    { name: 'User', label: 'User' },
    { name: 'MessageSquare', label: 'Message' },
    { name: 'Mail', label: 'Mail' },
    { name: 'Phone', label: 'Phone' },
    { name: 'MapPin', label: 'Map Pin' },
    { name: 'FileText', label: 'Document' },
    { name: 'Shield', label: 'Shield' },
    { name: 'CheckCircle2', label: 'Check Circle' },
    { name: 'AlertCircle', label: 'Alert' },
    { name: 'Clock', label: 'Clock' },
    { name: 'ExternalLink', label: 'External Link' },
    { name: 'Building2', label: 'Building' },
];

const ComplaintMethodModal = ({ isOpen, onClose, onSubmit, method, loading }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon_name: 'MessageSquare',
        type: 'link',
        href: '',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        if (method) {
            setFormData({
                ...method,
                is_active: method.is_active === 1 || method.is_active === true
            });
        } else {
            setFormData({
                title: '',
                description: '',
                icon_name: 'MessageSquare',
                type: 'link',
                href: '',
                display_order: 0,
                is_active: true
            });
        }
    }, [method, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {method ? 'Edit Complaint Method' : 'Add New Complaint Method'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Title
                        </label>
                        <input
                            required
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                            placeholder="e.g., Written Complaint"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Description
                        </label>
                        <textarea
                            required
                            rows="2"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                            placeholder="Enter description..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Type
                            </label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                            >
                                <option value="link">Direct Link/Action (href)</option>
                                <option value="popup">Opens Popup Form</option>
                            </select>
                        </div>
                        {formData.type === 'link' && (
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Link / Phone / Email (href)
                                </label>
                                <input
                                    type="text"
                                    value={formData.href || ''}
                                    onChange={(e) => setFormData({ ...formData, href: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                    placeholder="e.g., tel:9943430361 or mailto:..."
                                />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Status
                            </label>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={`w-full flex items-center justify-between px-6 py-3 rounded-2xl border transition-all ${formData.is_active
                                    ? 'bg-green-50 border-green-200 text-green-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500'
                                    }`}
                            >
                                <span className="font-semibold">{formData.is_active ? 'Active' : 'Inactive'}</span>
                                <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                                </div>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-4">
                            Select Icon
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                            {LUCIDE_ICONS.map((icon) => {
                                const IconComp = LucideIcons[icon.name];
                                return (
                                    <button
                                        key={icon.name}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon_name: icon.name })}
                                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${formData.icon_name === icon.name
                                            ? 'border-primary-500 bg-primary-50 text-primary-600'
                                            : 'border-slate-100 bg-white text-slate-400 hover:border-slate-200'
                                            }`}
                                    >
                                        <IconComp className="h-6 w-6" />
                                        <span className="text-[10px] whitespace-nowrap overflow-hidden text-ellipsis w-full">{icon.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </form>

                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                    >
                        {loading ? 'Saving...' : method ? 'Update Method' : 'Create Method'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplaintMethodModal;
